import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  Chip,
} from '@mui/material';
import {
  Timeline,
  TrendingUp,
  Group,
  Business,
  Assignment,
  Settings,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ActivityLog from '../../components/activities/ActivityLog';
import RetentionSettings from '../../components/settings/RetentionSettings';
import api from '../../services/api';
import RequireEmailVerification from '../../components/auth/RequireEmailVerification';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface ActivityStats {
  total_activities: number;
  activities_by_type: { [key: string]: number };
  activities_by_level: { [key: string]: number };
  activities_by_day: Array<{ date: string; count: number }>;
  most_active_users: Array<{ user_id: string; user_name: string; activity_count: number }>;
  most_active_clubs: Array<{ club_id: string; club_name: string; activity_count: number }>;
}

const ActivitiesPage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      setError(null);
      const response = await api.get('/activities/stats?days=30');
      setStats(response.data);
    } catch (err: any) {
      setError('Failed to load activity statistics');
      console.error('Error fetching stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const getLevelColor = (level: string) => {
    const colors: { [key: string]: string } = {
      info: '#0288d1',
      success: '#2e7d32',
      warning: '#ed6c02',
      error: '#d32f2f',
    };
    return colors[level] || '#666';
  };

  return (
    <RequireEmailVerification>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timeline />
            Activity Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Monitor and analyze system activities across your organization
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        {loadingStats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : stats && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
                <Card sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="white" variant="subtitle2">
                          Total Activities
                        </Typography>
                        <Typography color="white" variant="h4" sx={{ fontWeight: 600 }}>
                          {stats.total_activities.toLocaleString()}
                        </Typography>
                      </Box>
                      <Assignment sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
                <Card sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="white" variant="subtitle2">
                          Success Rate
                        </Typography>
                        <Typography color="white" variant="h4" sx={{ fontWeight: 600 }}>
                          {stats.activities_by_level.success ? 
                            Math.round((stats.activities_by_level.success / stats.total_activities) * 100) : 0}%
                        </Typography>
                      </Box>
                      <TrendingUp sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
                <Card sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="white" variant="subtitle2">
                          Active Users
                        </Typography>
                        <Typography color="white" variant="h4" sx={{ fontWeight: 600 }}>
                          {stats.most_active_users.length}
                        </Typography>
                      </Box>
                      <Group sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 250px', minWidth: 0 }}>
                <Card sx={{ background: 'linear-gradient(45deg, #9C27B0 30%, #BA68C8 90%)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="white" variant="subtitle2">
                          Active Clubs
                        </Typography>
                        <Typography color="white" variant="h4" sx={{ fontWeight: 600 }}>
                          {stats.most_active_clubs.length}
                        </Typography>
                      </Box>
                      <Business sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Charts */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
              <Box sx={{ flex: '1 1 65%', minWidth: '400px' }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Activity Trend (Last 30 Days)
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.activities_by_day}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#2196F3" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Box>

              <Box sx={{ flex: '1 1 30%', minWidth: '300px' }}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Activities by Level
                  </Typography>
                  <Box sx={{ mt: 3 }}>
                    {Object.entries(stats.activities_by_level).map(([level, count]) => (
                      <Box key={level} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {level}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {count}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          height: 8, 
                          borderRadius: 1, 
                          backgroundColor: '#e0e0e0',
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ 
                            height: '100%', 
                            width: `${(count / stats.total_activities) * 100}%`,
                            backgroundColor: getLevelColor(level),
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Box>

            {/* Top Users and Clubs */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: '350px' }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Most Active Users
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {stats.most_active_users.slice(0, 5).map((user, index) => (
                      <Box key={user.user_id} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            width: 24, 
                            color: 'text.secondary',
                            fontWeight: 600 
                          }}
                        >
                          {index + 1}.
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {user.user_name}
                        </Typography>
                        <Chip 
                          label={`${user.activity_count} activities`} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: '350px' }}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Most Active Clubs
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    {stats.most_active_clubs.slice(0, 5).map((club, index) => (
                      <Box key={club.club_id} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            width: 24, 
                            color: 'text.secondary',
                            fontWeight: 600 
                          }}
                        >
                          {index + 1}.
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {club.club_name}
                        </Typography>
                        <Chip 
                          label={`${club.activity_count} activities`} 
                          size="small" 
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        )}

        {/* Tabs for different activity views */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="All Activities" />
            <Tab label="My Activities" />
            <Tab label="System Activities" />
            {user?.is_platform_admin && <Tab label="Retention Settings" icon={<Settings />} iconPosition="start" />}
          </Tabs>
        </Paper>

        {/* Activity Logs */}
        {tabValue === 0 && (
          <ActivityLog showFilters={true} limit={100} />
        )}
        {tabValue === 1 && (
          <ActivityLog userId="current" showFilters={false} limit={50} title="My Recent Activities" />
        )}
        {tabValue === 2 && (
          <ActivityLog 
            showFilters={true} 
            limit={100} 
            title="System Activities"
          />
        )}
        {tabValue === 3 && user?.is_platform_admin && (
          <RetentionSettings />
        )}
      </Container>
    </RequireEmailVerification>
  );
};

export default ActivitiesPage;